import { useMemo } from 'react'
import { faCheck, faCalendar } from '@fortawesome/pro-regular-svg-icons'
import { Badge, Button, Card, Icon, Image, List, Text } from '@/ui'
import { TextColors, TypographyStyles } from '@/styles'
import { usePlanChangeEntry } from '@/features/user/settings/sections/plan-change/lib/usePlanChangeEntry'
import { ComparePlansLink } from './ComparePlansLink'
import { useReselector } from '@/utils/sharedHooks'
import {
  selectIsStarterPlan,
  selectIsHeardLitePlan,
} from '@/reducers/subscription.slice'

const NEXT_TAX_YEAR = 2026

const Perk = ({ text }: { text: string }) => (
  <List.Item className="flex items-center -mb-2">
    <Icon color="text-green-50" icon={faCheck} className="mr-2" size="xl" />
    <Text variant={TypographyStyles.BODY_SM}>{text}</Text>
  </List.Item>
)

export const OffSeasonUpsellCard = () => {
  const isLitePlan = useReselector(selectIsHeardLitePlan)
  const isStarterPlan = useReselector(selectIsStarterPlan)

  const planName = useMemo(() => {
    if (isStarterPlan) {
      return 'Starter'
    } else if (isLitePlan) {
      return 'Heard Lite'
    } else {
      return 'Basic'
    }
  }, [isStarterPlan, isLitePlan])
  const subtext = useMemo(() => {
    if (isStarterPlan) {
      return `Starter covers your bookkeeping. Upgrade to get quarterly tax estimates and have your ${NEXT_TAX_YEAR} annual tax return prepared and filed by a tax pro.`
    }
    return `${planName} covers your bookkeeping and quarterly taxes. Upgrade to have your ${NEXT_TAX_YEAR} annual tax return prepared and filed by a tax pro.`
  }, [planName, isStarterPlan])

  const { goToPlanChange } = usePlanChangeEntry()

  return (
    <Card className="p-4 bg-[linear-gradient(180deg,rgba(252,241,202,0.90)_0%,rgba(232,239,230,0)_100%)]">
      <Badge
        variant="secondary"
        color="green"
        icon={faCalendar}
        className="w-full flex"
      >
        Plan ahead: Upgrade by January 1, 2027 to have your 2026 annual tax
        return filed on-time.
      </Badge>
      <div className="flex gap-10 mt-6 flex-row">
        <div className="flex flex-col flex-[1] items-center">
          <Image
            src="https://heard-images.s3.amazonaws.com/assets/annual_taxes_compass.svg"
            alt="Annual Taxes"
            className="w-[200px] h-[200px]"
          />
          <div className="mt-16">
            <Button onClick={goToPlanChange}>Upgrade your Plan</Button>
          </div>
          {!isLitePlan && (
            <Text variant={TypographyStyles.BODY_SM} className="mt-4">
              Note: Once you upgrade, you cannot downgrade to your current plan.
            </Text>
          )}
        </div>

        <div className="flex-[2]">
          <Text variant={TypographyStyles.DISPLAY_MD}>
            Upgrade your {planName} plan
          </Text>
          <Text
            variant={TypographyStyles.BODY_SM}
            color={TextColors.MUTED}
            className="mt-2"
          >
            {subtext}
          </Text>
          <List className="-ml-4">
            <Perk text="Personal income tax prep and filing" />
            <Perk text="Document management and automated filing reminders" />
            <Perk text="Free tax extension filing support (as needed)" />
          </List>
          <ComparePlansLink />
        </div>
      </div>
    </Card>
  )
}
